<template>
  <f7-popup
    v-model:opened="isPopupOpened"
    class="main-internal-configurations"
    :close-on-escape="true"
  >
    <f7-page
      :page-content="false"
      :login-screen="!hasAccess"
    >
      <template #fixed>
        <TheNavbar
          :popup-close-btn-enable="true"
          :nav-title="t('internalConfigurations.title')"
        />
      </template>

      <template v-if="!hasAccess">
        <f7-page-content login-screen>
          <InternalCredentials
            :autofocus="isPopupOpened"
            @success="hasAccess = true"
          />
        </f7-page-content>
      </template>

      <template v-else>
        <f7-page-content class="internal-configurations-page-content">
          <InternalConfigurationsSidebar>
            <f7-button
              :active="currentTab === TABS.params"
              tab-link="#tab-params"
            >
              {{ t('common.params') }}
            </f7-button>

            <f7-button
              :active="currentTab === TABS.otherOptions"
              tab-link="#tab-other-options"
            >
              {{ t('common.otherOptions') }}
            </f7-button>

            <template #footer>
              <f7-button @click="handleLogout">
                <img src="@/static/icons/ic_logout.svg">
                <span class="margin-left-half">
                  {{ t('common.signOut') }}
                </span>
              </f7-button>
            </template>
          </InternalConfigurationsSidebar>

          <f7-tabs animated>
            <f7-tab
              id="tab-params"
              class="internal-configurations-page-content-tab"
              tab-active
              @tab:show="currentTab = TABS.params"
            >
              <InternalParamsConfiguration />
            </f7-tab>

            <f7-tab
              id="tab-other-options"
              class="internal-configurations-page-content-tab"
              @tab:show="currentTab = TABS.otherOptions"
            >
              <InternalOtherOptions />
            </f7-tab>
          </f7-tabs>
        </f7-page-content>
      </template>
    </f7-page>
  </f7-popup>
</template>

<script setup>
// @ts-check
import {
  defineAsyncComponent,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useSettingRef } from '@erpagroapp/settings';
import TheNavbar from '../TheNavbar.vue';
import InternalCredentials from './InternalCredentials.vue';
import InternalConfigurationsSidebar from './InternalConfigurationsSidebar.vue';

const InternalParamsConfiguration = defineAsyncComponent(() => import('./InternalParamsConfiguration.vue'));
const InternalOtherOptions = defineAsyncComponent(() => import('./InternalOtherOptions.vue'));

const { t } = useI18n();
const hasAccess = useSettingRef('ConfiguracionesInternas.AccesoHabilitado');

const isPopupOpened = ref(false);

const TABS = Object.freeze({
  params: 'params',
  otherOptions: 'otherOptions',
});
/** @type {import('vue').Ref<keyof TABS>} */
const currentTab = ref(TABS.params);

function handleLogout() {
  hasAccess.value = false;
  isPopupOpened.value = false;
}

/**
 * @param {KeyboardEvent} event
 * @returns {void}
 */
function handleKeyDown(event) {
  if (event.ctrlKey && event.altKey && event.code === 'Digit1') {
    isPopupOpened.value = !isPopupOpened.value;
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeyDown);
});
</script>

<style scoped lang="scss">
.main-internal-configurations {
  --f7-popup-tablet-width: 90vw;
  --f7-popup-tablet-height: 85vh;

  // Needed for both params configuration and other options
  --f7-toggle-active-color: var(--f7-theme-color);
  --f7-chip-height: 24px;
  --f7-chip-padding-horizontal: 6px;
  --f7-list-border-color: transparent;
  --f7-list-margin-vertical: 0;

  // TODO: Remove when migrated to new styles
  --f7-theme-color: var(--his-arandano);
  --f7-theme-color-rgb: 180, 180, 180;
  --f7-theme-color-shade: #575757;
  --f7-theme-color-tint: #8c8c8c;
  --f7-list-bg-color: transparent;
  --f7-list-item-title-text-color: #121212;
  --f7-button-border-radius: 4px;
  --f7-button-height: 36px;
  --f7-button-large-height: 44px;
  --f7-button-large-font-size: 15px;
  --f7-button-padding-horizontal: 16px;
  --f7-list-item-padding-vertical: 0.26rem;
  --f7-list-item-min-height: 44px;
  --f7-list-item-padding-horizontal: 12px;
  --f7-list-item-title-font-size: 1rem;
  --f7-list-item-border-color: rgb(0 0 0 / 12%);
  --f7-searchbar-input-padding-horizontal: 28px;
  --f7-searchbar-input-font-size: 16px;
  --f7-searchbar-search-icon-color: var(--his-mora-light);
  --f7-list-chevron-icon-color: var(--his-arandano-light);
  --f7-list-chevron-icon-font-size: 18px;
  --f7-list-font-size: 0.96rem;
  --f7-label-font-size: 0.9rem;
  --f7-label-line-height: 1.8;
  --f7-label-text-color: var(--his-arandano);

  // This popup must be on top of all others, the default for other popups is 11000
  z-index: 12000;

  @media (min-width: 530px) {
    :deep(.login-screen-content) {
      max-width: 320px;
      margin: auto;
    }
  }

  :deep(.list .item-label) {
    margin: 0 !important;
  }

  :deep(.item-input:not(.item-input-outline) .item-input-wrap::after) {
    height: 1px !important;
  }

  :deep(.navbar-bg) {
    --f7-navbar-bg-color: #333 !important;
  }

  :deep(.searchbar-found) {
    margin-top: unset;
  }

  :deep(.toggle-icon::after) {
    background: white !important;
  }

  :deep(.list .item-inner::after) {
    height: 1px;
  }

  //

  .internal-configurations-page-content {
    display: flex;
    flex-direction: row;
  }

  .internal-configurations-page-content-tab {
    overflow-y: hidden;
  }

  .tabs-animated-wrap > .tabs > .tab {
    display: flex;
    flex-direction: column;
  }
}
</style>
